import Vue from 'vue'
import { Component, Watch } from 'vue-property-decorator'
import { mixins } from 'vue-class-component'
import { State } from 'vuex-class'

import { ApolloResponse } from '@/interfaces/apollo'
import { Breadcrumb } from '@/interfaces/breadcrumbs'
import { SnackType } from '@/interfaces/snack'
import { Statement } from '@/modules/Statement/interfaces/statement'
import { showErrors, showNotification } from '@/utils/snack'
import { monthToDisplay } from '@/utils/dateTime'
import { tablePerPageOptions } from '@/utils/tablePerPageOptions'
import AppBreadcrumbs from '@/components/AppBreadcrumbs.vue'
import AppSearch from '@/components/AppSearch.vue'
import AppCheckbox from '@/components/AppCheckbox.vue'
import AppDialog from '@/components/AppDialog.vue'
import AppTextField from '@/components/AppTextField.vue'
import AppTextarea from '@/components/AppTextarea.vue'
import AppDatePicker from '@/components/AppDatePicker.vue'
import AppSelector from '@/components/AppSelector.vue'
import AppRefresh from '@/components/AppRefresh.vue'
import AppTextSearch from '@/components/AppTextSearch.vue'
import AppListSearchBtn from '@/components/AppListSearchBtn.vue'
import AppTableDate from '@/components/AppTableDate.vue'
import S3Handler from '@/mixins/s3'
import ExportExcel from '@/mixins/export-excel'
import { getStatements, pdfReader } from '@/modules/Statement/gql/statement.gql'
import { uploadFile } from '@/modules/File/gql/file.gql'
import { statementCarrier, statementCarrierKeys, statementCarrierValues } from '@/modules/Statement/helpers/statement'
import StatementDialog from '@/modules/Statement/scripts/statement-dialog'
import ListPage from '@/mixins/list-page'
import AppFilterButton from '@/components/AppFilterButton.vue'
import AppSimpleSelector from '@/components/AppSimpleSelector.vue'
import AppSimpleDatePicker from '@/components/AppSimpleDatePicker.vue'
import { PageState } from '../../../store/modules/page'

@Component({
  components: {
    AppBreadcrumbs,
    AppSearch,
    AppCheckbox,
    AppDialog,
    AppTextField,
    AppTextarea,
    AppDatePicker,
    AppSelector,
    AppRefresh,
    AppTextSearch,
    AppTableDate,
    AppListSearchBtn,
    AppFilterButton,
    AppSimpleSelector,
    AppSimpleDatePicker
  }
})
export default class StatementUploadList extends mixins(S3Handler, ExportExcel, StatementDialog, ListPage) {
  breadcrumbs: Breadcrumb[] = [
    {
      text: 'Accountings'
    }
  ]

  tableSortable = true

  tableColumn = [
    { field: 'id', title: 'Id', slots: { default: 'id_default' }, resizable: true, sortable: true },
    { title: 'Upload Date',
      field: 'createdAt',
      slots: {
        default: 'upload_date'
      },
      resizable: true,
      sortable: true
    },
    { title: 'Carrier',
      field: 'statementCarrier',
      slots: { default: ({ row }: any, h: any) => {
        return [
          h('span', row.statementCarrier ? statementCarrier[row.statementCarrier] : '-')
        ]
      }
      },
      resizable: true,
      sortable: true
    },
    { title: 'Month',
      field: 'yearAndMonth',
      slots: { default: ({ row }: any, h: any) => {
        return [
          h('span', row.yearAndMonth ? monthToDisplay(row.yearAndMonth) : '-')
        ]
      }
      },
      resizable: true,
      sortable: true
    },
    { title: 'Uploader',
      field: 'createdBy',
      slots: { default: ({ row }: any, h: any) => {
        return [
          h('span', row.createdBy ? row.createdBy.username : '-')
        ]
      }
      },
      resizable: true,
      sortable: true
    },
    { field: 'actions', title: 'Actions', slots: { default: 'action_default' }, fixed: 'right', width: 74 }
  ]

  tableOptions = {
    loading: false,
    tablePage: tablePerPageOptions(),
    sortBy: 'id',
    sortDesc: true
  }
  windowSize = {
    w: 0,
    h: 0,
    cW: 0
  }

  statements: Statement[] = []

  orderBy: {
    [key: string]: string
  } = {
    id: 'ID',
    uploadDate: 'UPLOAD_DATE',
    month: 'MONTH',
    carrier: 'CARRIER',
    creator: 'CREATOR'
  }

  pdfInput: {
    carrier: number;
    pdf: File | null;
    month: string;
  } = {
    carrier: -1,
    pdf: null,
    month: ''
  }

  uploadPDFDialog = {
    show: false,
    title: 'Statement Upload',
    disabled: false,
    noFileWarning: false
  }

  pdfFileTypes = `
    application/pdf
  `
  allowFileType = 'pdf'

  monthToDisplay = monthToDisplay

  fileId = -1

  statementCarrierOptions = statementCarrierValues
  statementCarrierKeys = statementCarrierKeys

  tableLoading = false

  @Watch('filesInfo', { deep: true })
  onFileInfoChanged (val: any) {
    if (val[0] && val[0].url) {
      this.uploadFile(this.fileId)
    }
  }

  @Watch('pdfInput.carrier')
  onCarrierChanged (val: number) {
    const { pdf } = this.pdfInput
    if (val === 0) {
      this.pdfFileTypes = 'application/pdf'
      this.allowFileType = 'pdf'
      if (pdf && pdf.type !== 'application/pdf') {
        this.pdfInput.pdf = null
      }
    } else if (val === 1) {
      this.pdfFileTypes = '.xlsx'
      this.allowFileType = 'xlsx'
      if (pdf && pdf.type !== 'application/vnd.ms-excel') {
        this.pdfInput.pdf = null
      }
    } else if (val === 2) {
      this.pdfFileTypes = '.pdf'
      this.allowFileType = 'pdf'
      if (pdf && pdf.type !== 'application/pdf') {
        this.pdfInput.pdf = null
      }
    }
  }

  @State(state => state.page) page!: PageState

  async created () {
    this.breadcrumbs.push({
      text: `Statement Upload`
    })
    await this.restorePage(this.page[this.$route.name || ''])
  }

  async mounted () {
    this.onResize()
    await this.restorePage(this.page[this.$route.name || ''])
    this.getStatements()
  }

  onResize () {
    const container = this.$refs.container as Vue & { clientWidth: number }
    this.windowSize = { w: window.innerWidth, h: window.innerHeight, cW: container.clientWidth }
  }

  routeTo (name: string, params: any) {
    this.$router.push({
      name,
      params
    })
  }

  async getStatements () {
    const { sortBy, sortDesc, tablePage } = this.options
    const { currentPage, pageSize } = tablePage
    const { startDate, endDate, month, carrierIndex } = this.filter
    const orderBy = this.orderBy[sortBy] || 'ID'
    const filter: {
      [key: string]: any;
    } = {}
    if (this.filter.startDate) {
      filter.startDate = this.filter.startDate.toString()
    }
    if (this.filter.endDate) {
      filter.endDate = this.filter.endDate.toString()
    }
    if (this.filter.yearAndMonth) {
      filter.yearAndMonth = this.filter.yearAndMonth.toString()
    }
    if (this.filter.carrierIndex !== null && !isNaN(this.filter.carrierIndex) && this.filter.carrierIndex !== -1) {
      filter.carrierName = statementCarrierKeys[carrierIndex]
    }
    this.tableLoading = true
    try {
      const result: ApolloResponse = await this.$apollo.query({
        query: getStatements,
        variables: {
          statementsFilter: filter,
          order: {
            orderBy: orderBy,
            asc: !sortDesc
          },
          paging: {
            offset: (currentPage - 1) * pageSize,
            limit: pageSize
          }
        }
      })
      if (!result.data) {
        showErrors(result.errors)
        return
      }
      this.statements = result.data.getStatements.statements
      this.tableLoading = false
      this.options.tablePage.total = result.data.getStatements.paging.totalCount
      this.storePage()
    } catch (e) {
      showErrors(e.graphQLErrors)
    }
  }

  uploadPDF () {
    this.uploadPDFDialog.show = true
  }

  async onPDFFileChanged (e: any) {
    let files = e.target.files || e.dataTransfer.files

    if (!files[0]) {
      return
    }
    this.uploadPDFDialog.noFileWarning = false
    this.pdfInput.pdf = files[0]
  }

  async uploadPDFRequests () {
    const newBookLabelValid = (this.$refs.formPDF as Vue & { validate: () => boolean }).validate()
    if (!newBookLabelValid) {
      return
    }

    if (!this.pdfInput.pdf) {
      this.uploadPDFDialog.noFileWarning = true
      return
    }

    this.uploadPDFDialog.disabled = true

    this.filesToUpload[0] = this.pdfInput.pdf

    if (!this.checkCredentialValid()) {
      await this.getSTSCredentials()
    }

    this.filePrefix = 'statements'
    this.fileType = 'STATEMENT_ATTACHMENT'
    const carrierName = this.statementCarrierKeys[this.pdfInput.carrier]

    try {
      const res = await this.$apollo.mutate({
        mutation: pdfReader,
        variables: {
          file: this.pdfInput.pdf,
          carrierName,
          month: this.pdfInput.month
        }
      })
      const { id } = res.data.pdfReader
      this.fileId = Number(id)
      await this.uploadFileToS3(0)
    } catch (e) {
      showErrors(e.graphQLErrors)
    }
  }

  async uploadFile (masterId: number) {
    const { url, notes, name, size, type } = this.filesInfo[0]
    try {
      const result = await this.$apollo.mutate({
        mutation: uploadFile,
        variables: {
          masterId: Number(masterId),
          masterType: 'STATEMENT',
          fileInput: {
            url: url,
            notes: notes,
            name: name,
            size: size.toString(),
            type: type
          }
        }
      })
      showNotification({
        msg: 'Statement Upload successfully',
        type: SnackType.Success
      })
      this.uploadPDFDialog.disabled = false
      this.uploadPDFDialog.show = false
      this.clearPDF()
      this.getStatements()
      this.filesInfo = []
    } catch (e) {
      showErrors(e.graphQLErrors)
    }
  }

  clearPDF () {
    this.pdfInput = {
      carrier: -1,
      pdf: null,
      month: ''
    }
    this.uploadPDFDialog.noFileWarning = false
  }

  clearFilter () {
    this.filter = {
      startDate: '',
      endDate: '',
      carrierIndex: -1,
      month: ''
    }
  }

  async refreshList () {
    await this.clearPage()
    await this.restorePage(this.page[this.$route.name || ''])
    this.getStatements()
  }

  sortTable (column: any) {
    this.options.sortBy = column.property
    this.options.sortDesc = column.order === 'desc'
    this.getStatements()
  }

  handlePageChange (pageData: any) {
    this.options.tablePage.currentPage = pageData.currentPage
    this.options.tablePage.pageSize = pageData.pageSize
    this.getStatements()
  }

  async deleteStatementConfirm () {
    await this.deleteStatementPost()
    this.getStatements()
  }

  routeToDetail (item: any) {
    this.routeTo('StatementDetail', {
      id: item.id
    })
  }

  async downloadFileHandler (file: any) {
    this.tableOptions.loading = true
    await this.downloadFile(file)
    this.tableOptions.loading = false
  }
}
